<template>
    <template v-if="store.selectedPackage !== null">
        <!-- Header -->
        <div class="is-flex is-align-items-center mb-5 gap-3">
            <h2 class="title is-4 mb-0 mr-3">
                {{ translate(`discipline_packages.discipline.${store.selectedPackage.discipline}`) }}
            </h2>
            <span class="tag is-light" :class="store.selectedPackage.isConfigured ? 'is-success' : 'is-warning'">
                {{
                    translate(store.selectedPackage.isConfigured ? "discipline_packages.configured" : "discipline_packages.not_configured")
                }}
            </span>
            <div class="ml-auto">
                <LoadingButton
                    :is-loading="store.saving"
                    :disabled="store.hasInvalidBracketConstraints"
                    class="button is-primary"
                    @click="store.save()"
                >
                    <span class="icon"><i class="fas fa-save"></i></span>
                    <span>{{ translate("common.save") }}</span>
                </LoadingButton>
            </div>
        </div>

        <!-- Section divider: Bracket Configuration -->
        <SectionDivider class="mb-4" icon="fas fa-sitemap" :label="translate('discipline_packages.bracket_configuration')" />

        <!-- Tab bar + Add override button -->
        <div class="is-flex is-align-items-flex-start mb-4 is-gap-2">
            <div class="tabs mb-0 is-flex-grow-1 tab-bar__tabs">
                <ul>
                    <li :class="{ 'is-active': store.activeTabIndex === null }">
                        <a @click="store.selectTab(null)">
                            {{ translate("discipline_packages.default_tab") }}
                        </a>
                    </li>
                    <li
                        v-for="(override, index) in store.disciplineOverrideConfigs"
                        :key="index"
                        :class="{ 'is-active': store.activeTabIndex === index }"
                    >
                        <a @click="store.selectTab(index)">
                            {{ override.name || translate("discipline_packages.override_tab_name", false, [index + 1]) }}
                        </a>
                    </li>
                </ul>
            </div>
            <button class="button tab-bar__add-btn" @click="store.addOverride()">
                <span class="icon"><i class="fa fa-plus"></i></span>
                <span>{{ translate("discipline_packages.add_override") }}</span>
            </button>
        </div>

        <!-- Default tab content -->
        <template v-if="store.activeTabIndex === null">
            <div class="columns">
                <div class="column is-3">
                    <div class="box has-text-grey-light config-panel__info-box">
                        <span class="icon is-large mb-3"><i class="fas fa-info-circle fa-2x"></i></span>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <p
                            v-html="
                                translate('discipline_packages.default_applies_to_all', false, [
                                    translate(`discipline_packages.discipline.${store.selectedPackage.discipline}`),
                                ])
                            "
                        ></p>
                    </div>
                </div>
                <div class="column is-9">
                    <div class="box mb-4">
                        <ScoreboardSection />
                    </div>
                    <div class="box">
                        <BracketRangesSection v-if="store.showsBracketSection" />
                        <p v-else class="has-text-grey-light">
                            <span class="icon-text">
                                <span class="icon"><i class="fas fa-info-circle"></i></span>
                                <span>{{ translate("discipline_packages.bracket_ranges_not_supported") }}</span>
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </template>

        <!-- Override tab content -->
        <template v-else>
            <div class="field mb-4">
                <div class="control">
                    <input
                        class="input"
                        type="text"
                        :placeholder="translate('discipline_packages.override_name_placeholder')"
                        :value="store.disciplineOverrideConfigs[store.activeTabIndex].name"
                        @input="store.updateOverrideName(store.activeTabIndex, ($event.target as HTMLInputElement).value)"
                    />
                </div>
            </div>

            <div class="columns is-desktop">
                <div class="column is-3 config-panel__sticky-col">
                    <div class="box">
                        <p class="heading has-text-grey-light mb-3">
                            <span class="icon-text">
                                <span class="icon"><i class="fas fa-tags"></i></span>
                                <span>{{ translate("discipline_packages.override_categories") }}</span>
                            </span>
                        </p>
                        <SelectionList
                            :options="store.categoriesAvailableForNewOverride"
                            :is-loading="store.eligibleCategoriesLoading"
                            :multi-select="true"
                            :comparing-function="(a: EligibleCategory, b: EligibleCategory) => a.id === b.id"
                            :model-value="selectedCategoryObjects"
                            @update:model-value="onCategorySelectionChange"
                        >
                            <template v-slot:option="{ option }">{{ option.path }}</template>
                        </SelectionList>
                        <div class="mt-3">
                            <button class="button is-danger is-small" @click="store.removeOverride(store.activeTabIndex)">
                                {{ translate("discipline_packages.remove_override") }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="column is-9">
                    <div class="box mb-4">
                        <ScoreboardSection :is-override="true" />
                    </div>
                    <div class="box">
                        <BracketRangesSection v-if="store.showsBracketSection" />
                        <p v-else class="has-text-grey-light">
                            <span class="icon-text">
                                <span class="icon"><i class="fas fa-info-circle"></i></span>
                                <span>{{ translate("discipline_packages.bracket_ranges_not_supported") }}</span>
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </template>
    </template>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../Common/translations";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { useDisciplinePackagesStore } from "./disciplinePackagesStore";
    import ScoreboardSection from "./ScoreboardSection.vue";
    import BracketRangesSection from "./BracketRangesSection.vue";
    import SelectionList from "../Common/SelectionList.vue";
    import SectionDivider from "../Common/SectionDivider.vue";
    import { EligibleCategory } from "../Api/disciplinePackages";

    const store = useDisciplinePackagesStore();

    const selectedCategoryObjects = computed((): EligibleCategory[] => {
        if (store.activeTabIndex === null) {
            return [];
        }
        const activeOverride = store.disciplineOverrideConfigs[store.activeTabIndex];
        if (!activeOverride) {
            return [];
        }
        return store.eligibleCategories.filter(cat => activeOverride.parameterIds.includes(cat.id));
    });

    function onCategorySelectionChange(selected: EligibleCategory[] | EligibleCategory | null | undefined): void {
        if (store.activeTabIndex === null) {
            return;
        }
        const activeOverride = store.disciplineOverrideConfigs[store.activeTabIndex];
        if (!activeOverride) {
            return;
        }
        if (Array.isArray(selected)) {
            activeOverride.parameterIds = selected.map(cat => cat.id);
        }
    }
</script>

<style scoped>
    .tab-bar__tabs {
        overflow: visible;
    }

    .tab-bar__add-btn {
        margin-top: 2px;
    }

    .config-panel__info-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        height: 100%;
    }

    @media screen and (min-width: 1024px) {
        .config-panel__sticky-col {
            position: sticky;
            top: 1rem;
            align-self: flex-start;
        }
    }
</style>
